import { type QuickViewSelectItem } from '../menus/quick-view-selector'
import { DEFAULT_INITIAL_STATE, type FilterStateStoreValues } from './filter-state-store'
import { type FindingsTableDropdownFilterProps } from './findings-table-dropdown-filter'

import { type CompositionBarSegments } from '../composition-bar/composition-utils'
export type {
  CompositionBarSegments,
  FixSegment,
  ScaSegment,
  TriageSegment,
} from '../composition-bar/composition-utils'

export type FindingQuickViewId =
  | 'all-findings'
  | 'analyzed'
  | 'validated'
  | 'ready-to-fix'
  | 'dismissable'
  | 'unanalyzed'
  | 'sca-analyzed'
  | 'sca-exploitable'
  | 'sca-not-exploitable'
  | 'sca-unanalyzed'

export type FindingQuickViewConfig = {
  id: FindingQuickViewId
  label: string
  defaultFilterState: FilterStateStoreValues
  visibleGroups: Required<Exclude<FindingsTableDropdownFilterProps['groups'], undefined>>
  compositionBarSegments: CompositionBarSegments
}

export const FINDING_QUICK_VIEW_CONFIGS: Record<FindingQuickViewId, FindingQuickViewConfig> = {
  'all-findings': {
    id: 'all-findings',
    label: 'All findings',
    defaultFilterState: {
      ...DEFAULT_INITIAL_STATE,
    },
    visibleGroups: {
      analyzed: 'hidden',
      hasFix: 'hidden',
      hasTriage: 'hidden',
      hasScaAnalysis: 'hidden',
      classification: 'hidden',
      fixResult: 'hidden',
      scaClassification: 'hidden',
      fixConfidence: 'hidden',
      severityUpdate: 'hidden',
      severity: 'enabled',
      suggestedSeverity: 'hidden',
    },
    compositionBarSegments: {},
  },
  analyzed: {
    id: 'analyzed',
    label: 'Analyzed findings',
    defaultFilterState: {
      ...DEFAULT_INITIAL_STATE,
      filterToAnalyzed: true,
    },
    visibleGroups: {
      analyzed: 'hidden',
      hasFix: 'hidden',
      hasTriage: 'hidden',
      hasScaAnalysis: 'hidden',
      classification: 'enabled',
      fixResult: 'enabled',
      scaClassification: 'hidden',
      fixConfidence: 'enabled',
      severityUpdate: 'enabled',
      severity: 'enabled',
      suggestedSeverity: 'enabled',
    },
    compositionBarSegments: {
      // All 6 triage segments and all 5 fix segments (including "Not triaged" and "No fix")
      triage: ['True positive', 'Likely true positive', 'Inconclusive', 'False positive', "Won't fix", 'Not triaged'],
      fix: ['High confidence', 'Medium confidence', 'Low confidence', 'No rating', 'No fix'],
    },
  },
  validated: {
    id: 'validated',
    label: 'Validated findings',
    defaultFilterState: {
      ...DEFAULT_INITIAL_STATE,
      hasTriage: true,
      filterToAnalyzed: true,
      classification: { ...DEFAULT_INITIAL_STATE.classification, true_positive: true, suspicious: true },
    },
    visibleGroups: {
      analyzed: 'hidden',
      hasFix: 'hidden',
      hasTriage: 'hidden',
      hasScaAnalysis: 'hidden',
      classification: 'disabled',
      fixResult: 'enabled',
      scaClassification: 'hidden',
      fixConfidence: 'enabled',
      severityUpdate: 'enabled',
      severity: 'enabled',
      suggestedSeverity: 'enabled',
    },
    compositionBarSegments: {
      // 2 triage segments (TP, Likely TP) and 5 fix segments
      triage: ['True positive', 'Likely true positive'],
      fix: ['High confidence', 'Medium confidence', 'Low confidence', 'No rating', 'No fix'],
    },
  },
  'ready-to-fix': {
    id: 'ready-to-fix',
    label: 'Ready to fix',
    defaultFilterState: {
      ...DEFAULT_INITIAL_STATE,
      filterToAnalyzed: true,
      classification: {
        ...DEFAULT_INITIAL_STATE.classification,
        true_positive: true,
        suspicious: true,
        inconclusive: true,
      },
      fixResult: { ...DEFAULT_INITIAL_STATE.fixResult, completed: true },
      patchStatus: { issued: true, merged: true },
    },
    visibleGroups: {
      analyzed: 'hidden',
      hasFix: 'hidden',
      hasTriage: 'hidden',
      hasScaAnalysis: 'hidden',
      classification: 'disabled',
      fixResult: 'disabled',
      scaClassification: 'hidden',
      fixConfidence: 'enabled',
      severityUpdate: 'enabled',
      severity: 'enabled',
      suggestedSeverity: 'enabled',
    },
    compositionBarSegments: {
      // 3 triage segments matching locked classification filters, 4 fix segments (no "No fix")
      triage: ['True positive', 'Likely true positive', 'Inconclusive'],
      fix: ['High confidence', 'Medium confidence', 'Low confidence', 'No rating'],
    },
  },
  dismissable: {
    id: 'dismissable',
    label: 'Dismissable findings',
    defaultFilterState: {
      ...DEFAULT_INITIAL_STATE,
      hasTriage: true,
      filterToAnalyzed: true,
      classification: { ...DEFAULT_INITIAL_STATE.classification, false_positive: true, wont_fix: true },
    },
    visibleGroups: {
      analyzed: 'hidden',
      hasFix: 'hidden',
      hasTriage: 'hidden',
      hasScaAnalysis: 'hidden',
      classification: 'disabled',
      fixResult: 'hidden',
      scaClassification: 'hidden',
      fixConfidence: 'hidden',
      severityUpdate: 'hidden',
      severity: 'enabled',
      suggestedSeverity: 'hidden',
    },
    compositionBarSegments: {
      // 2 triage segments (FP, Won't fix) + empty fix bar with explanatory message
      triage: ['False positive', "Won't fix"],
      fix: [],
    },
  },
  unanalyzed: {
    id: 'unanalyzed',
    label: 'Unanalyzed findings',
    defaultFilterState: {
      ...DEFAULT_INITIAL_STATE,
      filterToUnanalyzed: true,
    },
    visibleGroups: {
      analyzed: 'hidden',
      hasFix: 'hidden',
      hasTriage: 'hidden',
      hasScaAnalysis: 'hidden',
      classification: 'hidden',
      fixResult: 'hidden',
      scaClassification: 'hidden',
      fixConfidence: 'hidden',
      severityUpdate: 'hidden',
      severity: 'enabled',
      suggestedSeverity: 'hidden',
    },
    compositionBarSegments: {
      triage: [],
    },
  },
  'sca-analyzed': {
    id: 'sca-analyzed',
    label: 'Analyzed findings',
    defaultFilterState: {
      ...DEFAULT_INITIAL_STATE,
      hasScaAnalysis: true,
      filterToAnalyzed: true,
    },
    visibleGroups: {
      analyzed: 'hidden',
      hasFix: 'hidden',
      hasTriage: 'hidden',
      hasScaAnalysis: 'disabled',
      classification: 'hidden',
      fixResult: 'hidden',
      scaClassification: 'enabled',
      fixConfidence: 'hidden',
      severityUpdate: 'enabled',
      severity: 'enabled',
      suggestedSeverity: 'enabled',
    },
    compositionBarSegments: {
      // Show 3 SCA segments (Exploitable, Not exploitable, Potentially exploitable)
      sca: ['Exploitable', 'Not exploitable', 'Potentially exploitable'],
    },
  },
  'sca-exploitable': {
    id: 'sca-exploitable',
    label: 'Exploitable findings',
    defaultFilterState: {
      ...DEFAULT_INITIAL_STATE,
      hasScaAnalysis: true,
      filterToAnalyzed: true,
      scaClassification: { ...DEFAULT_INITIAL_STATE.scaClassification, exploitable: true },
    },
    visibleGroups: {
      analyzed: 'hidden',
      hasFix: 'hidden',
      hasTriage: 'hidden',
      hasScaAnalysis: 'disabled',
      classification: 'hidden',
      fixResult: 'hidden',
      scaClassification: 'disabled',
      fixConfidence: 'hidden',
      severityUpdate: 'enabled',
      severity: 'enabled',
      suggestedSeverity: 'enabled',
    },
    compositionBarSegments: {
      // Show only Exploitable segment
      sca: ['Exploitable'],
    },
  },
  'sca-not-exploitable': {
    id: 'sca-not-exploitable',
    label: 'Not exploitable findings',
    defaultFilterState: {
      ...DEFAULT_INITIAL_STATE,
      hasScaAnalysis: true,
      filterToAnalyzed: true,
      scaClassification: { ...DEFAULT_INITIAL_STATE.scaClassification, 'not-exploitable': true },
    },
    visibleGroups: {
      analyzed: 'hidden',
      hasFix: 'hidden',
      hasTriage: 'hidden',
      hasScaAnalysis: 'disabled',
      classification: 'hidden',
      fixResult: 'hidden',
      scaClassification: 'disabled',
      fixConfidence: 'hidden',
      severityUpdate: 'enabled',
      severity: 'enabled',
      suggestedSeverity: 'enabled',
    },
    compositionBarSegments: {
      // Show only Not exploitable segment
      sca: ['Not exploitable'],
    },
  },
  'sca-unanalyzed': {
    id: 'sca-unanalyzed',
    label: 'Unanalyzed findings',
    defaultFilterState: {
      ...DEFAULT_INITIAL_STATE,
      filterToScaUnanalyzed: true,
    },
    visibleGroups: {
      analyzed: 'hidden',
      hasFix: 'hidden',
      hasTriage: 'hidden',
      hasScaAnalysis: 'hidden',
      classification: 'hidden',
      fixResult: 'hidden',
      scaClassification: 'hidden',
      fixConfidence: 'hidden',
      severityUpdate: 'hidden',
      severity: 'enabled',
      suggestedSeverity: 'hidden',
    },
    compositionBarSegments: {
      // Show "Not researched" segment for unresearched findings
      sca: ['Not researched'],
    },
  },
}

export const DEFAULT_FINDING_QUICK_VIEW_ID: FindingQuickViewId = 'analyzed'
export const DEFAULT_SCA_FINDING_QUICK_VIEW_ID: FindingQuickViewId = 'sca-analyzed'

export const FINDING_QUICK_VIEW_IDS = Object.keys(FINDING_QUICK_VIEW_CONFIGS) as FindingQuickViewId[]

export const SAST_FINDING_QUICK_VIEW_IDS: FindingQuickViewId[] = [
  'analyzed',
  'validated',
  'ready-to-fix',
  'dismissable',
  'unanalyzed',
]

export const SCA_FINDING_QUICK_VIEW_IDS: FindingQuickViewId[] = [
  'sca-analyzed',
  'sca-exploitable',
  'sca-not-exploitable',
  'sca-unanalyzed',
]

export const ALL_FINDING_QUICK_VIEW_IDS = [...SAST_FINDING_QUICK_VIEW_IDS, ...SCA_FINDING_QUICK_VIEW_IDS] as const

export function getFindingQuickViewConfig(id: FindingQuickViewId): FindingQuickViewConfig {
  return FINDING_QUICK_VIEW_CONFIGS[id]
}

export function createFindingQuickViewSelectItem(
  id: FindingQuickViewId,
  count?: number
): QuickViewSelectItem<FindingQuickViewId> {
  const config = getFindingQuickViewConfig(id)
  return {
    value: id,
    label: config.label,
    count,
  }
}
